import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import partnerService from '../services/partnerService';
import chatService from '../services/chatService';
import { activityService } from '../services/activityService';
import { Search, Filter, ChevronDown, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { AgentCard } from './ui/AgentCard';
import './PartnerManager.css';

const PartnerManager = ({ onChatCreated }) => {
  const { user } = useContext(AuthContext);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [creatingChat, setCreatingChat] = useState(null); // 正在創建聊天的夥伴ID
  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false
  });
  const [formData, setFormData] = useState({
    // 一、核心配置
    name: '',
    description: '',
    category: 'general',
    tags: '',
    // 二、角色任務與行動原則
    primaryTask: '',
    coreMotivation: '',
    openingApproach: '',
    responseExamples: [''],
    // 三、知識庫與專業
    professionalSkills: '',
    backgroundKnowledge: '',
    keyTerminology: '',
    // 四、行為與溝通風格
    personality: '友善、耐心',
    communicationStyleDetail: '溫和、鼓勵式',
    interactionMethod: '使用問題導向為主的對話框架，適合引導使用者思考和學習',
    guardrails: '',
    // 回應時機
    responseMode: 'always',
    responseConditions: '',
    availabilitySchedule: '',
    responseDelay: '',
    isPublicTemplate: false
  });

  // 分類選項
  const categories = [
    { value: 'all', label: '全部' },
    { value: 'general', label: '通用教學' },
    { value: 'science', label: '科學' },
    { value: 'math', label: '數學' },
    { value: 'language', label: '語言' },
    { value: 'programming', label: '程式設計' },
    { value: 'art', label: '藝術' },
    { value: 'music', label: '音樂' },
    { value: 'sports', label: '體育' },
    { value: 'other', label: '其他' }
  ];

  // 排序選項
  const sortOptions = [
    { value: 'newest', label: '最新' },
    { value: 'oldest', label: '最舊' },
    { value: 'name', label: '名稱' },
    { value: 'category', label: '分類' }
  ];

  useEffect(() => {
    if (user) {
      fetchPartners();
    }
  }, [user]);

  const fetchPartners = async (page = currentPage) => {
    try {
      setLoading(true);
      const response = await partnerService.getAllPartners(page, 10);
      
      // 檢查回應格式，支援新舊兩種格式
      if (response.partners && response.pagination) {
        // 新的分頁格式
        setPartners(response.partners);
        setPagination(response.pagination);
        setCurrentPage(response.pagination.currentPage);
      } else {
        // 舊格式（向後兼容）
        setPartners(response);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: response.length,
          limit: 10,
          hasNext: false,
          hasPrev: false
        });
      }
      setError(null);
    } catch (err) {
      setError('無法載入夥伴列表');
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  // 分頁處理函數
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchPartners(newPage);
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrev) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNext) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        tags: formData.tags,
        roleTask: {
          primaryTask: formData.primaryTask,
          coreMotivation: formData.coreMotivation,
          openingApproach: formData.openingApproach,
          responseExamples: formData.responseExamples.filter(x => x.trim())
        },
        knowledgeBase: {
          professionalSkills: formData.professionalSkills || '',
          backgroundKnowledge: formData.backgroundKnowledge,
          keyTerminology: formData.keyTerminology ? formData.keyTerminology.split('\n').filter(x => x.trim()) : []
        },
        communicationStyle: {
          personality: formData.personality,
          communicationStyle: formData.communicationStyleDetail,
          interactionMethod: formData.interactionMethod,
          guardrails: formData.guardrails ? formData.guardrails.split('\n').filter(x => x.trim()) : []
        },
        responseTimeline: {
          responseMode: formData.responseMode,
          responseConditions: formData.responseConditions,
          availabilitySchedule: formData.availabilitySchedule,
          responseDelay: formData.responseDelay
        },
        isPublicTemplate: formData.isPublicTemplate
      };

      const newPartner = await partnerService.createPartner(payload);
      
      setPartners([newPartner, ...partners]);
      resetForm();
      setError(null);
    } catch (err) {
      setError(err.message || '創建夥伴失敗');
      console.error('Error creating partner:', err);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        tags: formData.tags,
        roleTask: {
          primaryTask: formData.primaryTask,
          coreMotivation: formData.coreMotivation,
          openingApproach: formData.openingApproach,
          responseExamples: formData.responseExamples.filter(x => x.trim())
        },
        knowledgeBase: {
          professionalSkills: formData.professionalSkills || '',
          backgroundKnowledge: formData.backgroundKnowledge,
          keyTerminology: formData.keyTerminology ? formData.keyTerminology.split('\n').filter(x => x.trim()) : []
        },
        communicationStyle: {
          personality: formData.personality,
          communicationStyle: formData.communicationStyleDetail,
          interactionMethod: formData.interactionMethod,
          guardrails: formData.guardrails ? formData.guardrails.split('\n').filter(x => x.trim()) : []
        },
        responseTimeline: {
          responseMode: formData.responseMode,
          responseConditions: formData.responseConditions,
          availabilitySchedule: formData.availabilitySchedule,
          responseDelay: formData.responseDelay
        },
        isPublicTemplate: formData.isPublicTemplate
      };

      const updatedPartner = await partnerService.updatePartner(editingPartner.id, payload);
      
      setPartners(partners.map(p => p.id === editingPartner.id ? updatedPartner : p));
      resetForm();
      setError(null);
    } catch (err) {
      setError(err.message || '更新夥伴失敗');
      console.error('Error updating partner:', err);
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('確定要刪除這個夥伴嗎？')) return;
    
    try {
      await partnerService.deletePartner(partnerId);
      setPartners(partners.filter(p => p.id !== partnerId));
      setError(null);
    } catch (err) {
      setError('刪除夥伴失敗');
      console.error('Error deleting partner:', err);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      description: partner.description || '',
      category: partner.category || 'general',
      tags: partner.tags || '',
      // 二、角色任務與行動原則
      primaryTask: partner.roleTask?.primaryTask || '',
      coreMotivation: partner.roleTask?.coreMotivation || '',
      openingApproach: partner.roleTask?.openingApproach || '',
      responseExamples: partner.roleTask?.responseExamples?.length > 0 ? partner.roleTask.responseExamples : [''],
      // 三、知識庫與專業
      professionalSkills: partner.knowledgeBase?.professionalSkills || '',
      backgroundKnowledge: partner.knowledgeBase?.backgroundKnowledge || '',
      keyTerminology: partner.knowledgeBase?.keyTerminology ? partner.knowledgeBase.keyTerminology.join('\n') : '',
      // 四、行為與溝通風格
      personality: partner.communicationStyle?.personality || '友善、耐心',
      communicationStyleDetail: partner.communicationStyle?.communicationStyle || '溫和、鼓勵式',
      interactionMethod: partner.communicationStyle?.interactionMethod || '引導式學習',
      guardrails: partner.communicationStyle?.guardrails ? partner.communicationStyle.guardrails.join('\n') : '',
      // Response Timeline fields
      responseMode: partner.responseTimeline?.responseMode || 'always',
      responseConditions: partner.responseTimeline?.responseConditions || '',
      availabilitySchedule: partner.responseTimeline?.availabilitySchedule || '',
      responseDelay: partner.responseTimeline?.responseDelay || '',
      isPublicTemplate: partner.isPublicTemplate || false
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      // 一、核心配置
      name: '',
      description: '',
      category: 'general',
      tags: '',
      // 二、角色任務與行動原則
      primaryTask: '',
      coreMotivation: '',
      openingApproach: '',
      responseExamples: [''],
      // 三、知識庫與專業
      professionalSkills: '',
      backgroundKnowledge: '',
      keyTerminology: '',
      // 四、行為與溝通風格
      personality: '友善、耐心',
      communicationStyleDetail: '溫和、鼓勵式',
      interactionMethod: '使用問題導向為主的對話框架，適合引導使用者思考和學習',
      guardrails: '',
      // 回應時機
      responseMode: 'always',
      responseConditions: '',
      availabilitySchedule: '',
      responseDelay: '',
      isPublicTemplate: false
    });
    setEditingPartner(null);
    setShowForm(false);
    setShowAdvancedSettings(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 處理回應範例的新增
  const handleAddResponseExample = () => {
    setFormData(prev => ({
      ...prev,
      responseExamples: [...prev.responseExamples, '']
    }));
  };

  // 處理回應範例的刪除
  const handleRemoveResponseExample = (index) => {
    setFormData(prev => ({
      ...prev,
      responseExamples: prev.responseExamples.filter((_, i) => i !== index)
    }));
  };

  // 處理回應範例的修改
  const handleResponseExampleChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      responseExamples: prev.responseExamples.map((item, i) => i === index ? value : item)
    }));
  };

  const handleChatWithPartner = async (partner) => {
    try {
      setCreatingChat(partner.id);
      setError(null);

      // 創建與夥伴的聊天活動和小組
      const chatData = await chatService.createPartnerChat(partner);
      
      // 通知父組件有新的聊天創建
      if (onChatCreated) {
        onChatCreated(chatData);
      }

      // 顯示成功訊息
      alert(`已成功創建與 ${partner.name} 的聊天室！即將為您打開聊天頁面...`);
      
      // 短暫延遲後導航到聊天室，讓用戶看到成功訊息
      setTimeout(() => {
        chatService.navigateToChat(chatData.activity, chatData.group);
      }, 1000);
      
    } catch (err) {
      setError(err.message || '創建聊天室失敗');
      console.error('與夥伴聊天失敗:', err);
    } finally {
      setCreatingChat(null);
    }
  };

  const handleAddToActivity = async (partner, activity) => {
    try {
      setError(null);
      
      const result = await activityService.addPartnerToActivity(
        partner.id,
        partner.name,
        activity.id,
        {
          description: partner.description,
          category: partner.category,
          tags: partner.tags
        }
      );

      if (result.success) {
        alert(`已成功將「${partner.name}」添加到活動「${activity.title}」中！`);
      } else {
        setError(result.error);
        alert(`添加失敗：${result.error}`);
      }
    } catch (err) {
      const errorMessage = '添加夥伴到活動失敗';
      setError(errorMessage);
      console.error('添加夥伴到活動失敗:', err);
      alert(`添加失敗：${errorMessage}`);
    }
  };

  // 當搜索條件改變時，重置到第一頁
  useEffect(() => {
    if (searchTerm || selectedCategory !== 'all') {
      setCurrentPage(1);
      fetchPartners(1);
    }
  }, [searchTerm, selectedCategory, sortBy]);

  // 過濾和排序夥伴（針對當前頁面的結果進行客戶端過濾）
  const filteredAndSortedPartners = React.useMemo(() => {
    let filtered = partners;

    // 搜索過濾
    if (searchTerm) {
      filtered = filtered.filter(partner => 
        partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.tags?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 分類過濾
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(partner => partner.category === selectedCategory);
    }

    // 排序
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [partners, searchTerm, selectedCategory, sortBy]);

  if (!user) {
    return <div className="partner-manager-container">請先登入</div>;
  }

  if (loading) {
    return <div className="partner-manager-container loading">載入中...</div>;
  }

  return (
    <div className="partner-manager-container">
      {/* Header */}
      <div className="partner-header">
        <div className="header-content">
          <h1 className="page-title">AI 夥伴市場</h1>
          <p className="page-subtitle">探索並建立您的教學夥伴</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={20} />
          建立新夥伴
        </button>
      </div>

      {/* Toolbar */}
      <div className="partner-toolbar">
        <div className="search-section">
          <div className="search-bar">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="搜尋夥伴..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <div className="filter-dropdown">
              <Filter size={16} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <ChevronDown size={16} />
            </div>
            
            <div className="filter-dropdown">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={16} />
            </div>
          </div>
        </div>
        
        <div className="results-info">
          找到 {pagination.totalCount} 個夥伴 
          {pagination.totalPages > 1 && (
            <span className="page-info">
              （第 {pagination.currentPage} 頁，共 {pagination.totalPages} 頁）
            </span>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Partners Grid */}
      <div className="partners-grid">
        {filteredAndSortedPartners.length === 0 ? (
          <div className="empty-state">
            {partners.length === 0 ? (
              <>
                <h3>還沒有任何夥伴</h3>
                <p>創建您的第一個 AI 教學夥伴開始探索吧！</p>
                <button 
                  className="btn-primary"
                  onClick={() => setShowForm(true)}
                >
                  創建第一個夥伴
                </button>
              </>
            ) : (
              <>
                <h3>沒有找到符合條件的夥伴</h3>
                <p>嘗試調整搜索條件或建立新的夥伴</p>
              </>
            )}
          </div>
        ) : (
          filteredAndSortedPartners.map(partner => (
            <AgentCard
              key={partner.id}
              agent={partner}
              onEdit={partner.userId === user.id ? handleEdit : null}
              onDelete={partner.userId === user.id ? handleDelete : null}
              onUse={handleChatWithPartner}
              onAddToActivity={handleAddToActivity}
              isCreatingChat={creatingChat === partner.id}
              canEdit={partner.userId === user.id}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination">
            <button 
              className={`pagination-btn prev ${!pagination.hasPrev ? 'disabled' : ''}`}
              onClick={handlePrevPage}
              disabled={!pagination.hasPrev}
            >
              <ChevronLeft size={16} />
              上一頁
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page ${pageNum === pagination.currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className={`pagination-btn next ${!pagination.hasNext ? 'disabled' : ''}`}
              onClick={handleNextPage}
              disabled={!pagination.hasNext}
            >
              下一頁
              <ChevronRight size={16} />
            </button>
          </div>
          
          <div className="pagination-info">
            第 {(pagination.currentPage - 1) * pagination.limit + 1} - {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} 項，共 {pagination.totalCount} 項
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="partner-form-overlay">
          <div className="partner-form">
            <h3>{editingPartner ? '編輯夥伴' : '新增夥伴'}</h3>
            <form onSubmit={editingPartner ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>名稱 *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>描述 （公開可見的部分）</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              {/* 基礎設定 - 夥伴個性化 */}
              <div className="form-section basic-settings">
                <h4>基礎設定 — 夥伴個性化</h4>
                <p className="section-description">定義夥伴的基本資訊與個性特徵</p>
                
                {/* 分類與標籤 */}
                <div className="settings-group">
                  <h5>分類與標籤</h5>
                  <div className="form-group">
                    <label>分類</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      {categories.filter(cat => cat.value !== 'all').map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>標籤 (以逗號分隔)</label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="例：AI、教育、數學"
                    />
                  </div>
                </div>

                {/* 個性與背景 */}
                <div className="settings-group">
                  <h5>個性與背景</h5>
                  <div className="form-group">
                    <label>性格</label>
                    <input
                      type="text"
                      name="personality"
                      value={formData.personality}
                      onChange={handleInputChange}
                      placeholder="例：友善、耐心、專業、幽默"
                    />
                  </div>
                  <div className="form-group">
                    <label>背景與知識</label>
                    <textarea
                      name="backgroundKnowledge"
                      value={formData.backgroundKnowledge}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="例：AI 博士生，擁有 5 年以上軟體開發經驗，專精於 AI/ML"
                    />
                  </div>
                  <div className="form-group">
                    <label>開場方式</label>
                    <input
                      type="text"
                      name="openingApproach"
                      value={formData.openingApproach}
                      onChange={handleInputChange}
                      placeholder="例：嘿！有什麼有趣的技術挑戰嗎？"
                    />
                  </div>
                </div>
              </div>

              {/* 進階設定 */}
              <div className="form-section advanced-settings">
                <div className="advanced-header" onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}>
                  <h4>進階設定</h4>
                  <span className={`toggle-icon ${showAdvancedSettings ? 'expanded' : ''}`}>▼</span>
                </div>
                
                {showAdvancedSettings && (
                  <div className="advanced-content">
                    {/* 思考邏輯 (Thinking & Knowledge) */}
                    <div className="settings-group">
                      <h5>思考邏輯</h5>
                      <p className="group-description">定義夥伴的思考方式與專業知識</p>
                      <div className="form-group">
                        <label>首要任務</label>
                        <input
                          type="text"
                          name="primaryTask"
                          value={formData.primaryTask}
                          onChange={handleInputChange}
                          placeholder="例：技術可行性評估與解決方案提供"
                        />
                      </div>
                      <div className="form-group">
                        <label>核心動機</label>
                        <textarea
                          name="coreMotivation"
                          value={formData.coreMotivation}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="例：追求實用、高效、創新的技術解決方案"
                        />
                      </div>
                      <div className="form-group">
                        <label>專業知識</label>
                        <textarea
                          name="professionalSkills"
                          value={formData.professionalSkills}
                          onChange={handleInputChange}
                          rows="5"
                          placeholder="例：精通合作論證（Collaborative Argumentation）理論與實踐，基於 Toulmin (1958) 的論證模型，熟悉主張（Claim）、證據（Data）、論據（Warrant）的結構分析。深諳 Andriessen & Baker (2014) 提出的合作論證框架，擅長引導學習者透過對話建構共識、識別論點與證據的關係。熟悉 Asterhan & Schwarz (2016) 關於論證式對話促進概念改變的研究，能有效促進多方觀點的整合與批判性分析。"
                        />
                      </div>
                      <div className="form-group">
                        <label>關鍵術語（每行一個）</label>
                        <textarea
                          name="keyTerminology"
                          value={formData.keyTerminology}
                          onChange={handleInputChange}
                          rows="4"
                          placeholder="例：&#10;深度學習 (Deep Learning)&#10;神經網路 (Neural Networks)&#10;RESTful API"
                        />
                      </div>
                      <div className="form-group">
                        <label>互動/教學方法</label>
                        <textarea
                          name="interactionMethod"
                          value={formData.interactionMethod}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="例：引導式問答，先理解需求，再提供具體可行的方案"
                        />
                      </div>
                    </div>

                    {/* 行為準則 (Actions & Behavior) */}
                    <div className="settings-group">
                      <h5>行為準則</h5>
                      <p className="group-description">定義夥伴的回應方式與行為規範</p>
                      <div className="form-group">
                        <label>溝通風格</label>
                        <textarea
                          name="communicationStyleDetail"
                          value={formData.communicationStyleDetail}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="例：輕鬆幽默但專業，善於用簡單的比喻解釋複雜概念"
                        />
                      </div>
                      <div className="form-group">
                        <label>回應範例（支援 Markdown）</label>
                        <div className="dynamic-list">
                          {formData.responseExamples.map((example, index) => (
                            <div key={index} className="dynamic-list-item">
                              <textarea
                                value={example}
                                onChange={(e) => handleResponseExampleChange(index, e.target.value)}
                                placeholder={`範例 ${index + 1}：支援 Markdown 格式\n例如：「這個想法不錯！從技術角度來看...\n\n**重點**：可以使用 *斜體*、**粗體** 等格式」`}
                                rows="3"
                              />
                              {formData.responseExamples.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-remove"
                                  onClick={() => handleRemoveResponseExample(index)}
                                  title="刪除此範例"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn-add"
                            onClick={handleAddResponseExample}
                          >
                            + 新增回應範例
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>判定時機方式</label>
                        <select
                          name="responseMode"
                          value={formData.responseMode}
                          onChange={handleInputChange}
                        >
                          <option value="always">每次都回應</option>
                          <option value="selective">回應判斷</option>
                        </select>
                      </div>
                      
                      {formData.responseMode === 'selective' && (
                        <div className="form-group">
                          <label>回應判斷條件</label>
                          <textarea
                            name="responseConditions"
                            value={formData.responseConditions}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="例：當學生提出問題時、需要澄清概念時、學習遇到困難時"
                          />
                        </div>
                      )}
                      
                      <div className="form-group" style={{display: 'none'}}>
                        <label>可用時間安排</label>
                        <input
                          type="text"
                          name="availabilitySchedule"
                          value={formData.availabilitySchedule}
                          onChange={handleInputChange}
                          placeholder="例：週一到週五 9-17點、24小時全天候"
                        />
                      </div>
                      
                      <div className="form-group" style={{display: 'none'}}>
                        <label>回應延遲設定</label>
                        <input
                          type="text"
                          name="responseDelay"
                          value={formData.responseDelay}
                          onChange={handleInputChange}
                          placeholder="例：立即回應、2-5分鐘後、思考片刻後"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>禁止事項（每行一個）</label>
                        <textarea
                          name="guardrails"
                          value={formData.guardrails}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="例：&#10;不提供未經驗證的理論方案&#10;避免過度複雜化簡單問題"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isPublicTemplate"
                    checked={formData.isPublicTemplate}
                    onChange={handleInputChange}
                  />
                  公開模板
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPartner ? '更新' : '創建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerManager; 